import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Heart, MessageCircle, MoreHorizontal, Trash2, Lock, Flag, UserX } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CommentsSection from './CommentsSection';
import PostImage from './PostImage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const REACTION_TYPES = [
  { type: 'love', emoji: '❤️', label: 'Love' },
  { type: 'like', emoji: '👍', label: 'Like' },
  { type: 'haha', emoji: '😂', label: 'Haha' },
  { type: 'wow', emoji: '😮', label: 'Wow' },
  { type: 'sad', emoji: '😢', label: 'Sad' },
];

export default function PostCard({ post, currentUser, onDeleted, onReactionUpdated, onBlocked }) {
  const [showComments, setShowComments] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('inappropriate');
  const [reportDetails, setReportDetails] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  const isOwnPost = post.user_id === currentUser?.id;
  const totalReactions = Object.values(post.reactions_count || {}).reduce((a, b) => a + b, 0);
  const canInteract = currentUser?.role === 'admin' || currentUser?.role === 'member' || 
    (currentUser?.role === 'guest' && currentUser?.access_level === 'can_comment');

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/posts/${post.id}`, { withCredentials: true });
      onDeleted(post.id);
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setDeleting(false);
    }
  };

  const handleReaction = async (type) => {
    try {
      await axios.post(`${API}/posts/${post.id}/reactions`, { type }, { withCredentials: true });
      onReactionUpdated(post.id);
    } catch (error) {
      toast.error('Failed to react');
    }
    setShowReactions(false);
  };

  const submitReport = async () => {
    setSubmittingReport(true);
    try {
      await axios.post(`${API}/reports`, {
        target_type: 'post',
        target_id: post.id,
        reason: reportReason,
        details: reportDetails,
      }, { withCredentials: true });
      toast.success('Report sent. Your family admin will review it.');
      setReportOpen(false);
      setReportDetails('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit report');
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleBlock = async () => {
    if (!window.confirm(`Block ${post.user_name}? You won't see their posts or comments.`)) return;
    try {
      await axios.post(`${API}/block`, { user_id: post.user_id }, { withCredentials: true });
      toast.success(`${post.user_name} is now blocked`);
      if (onBlocked) onBlocked(post.user_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not block');
    }
  };

  return (
    <article className="card-soft" data-testid={`post-${post.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-11 w-11 border-2 border-teal-100">
            <AvatarFallback className="bg-teal-600 text-white font-medium">
              {getInitials(post.user_name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-semibold text-stone-900">{post.user_name}</p>
            <div className="flex items-center gap-2 text-sm text-stone-500">
              <span>{formatDate(post.created_at)}</span>
              {post.visibility === 'family_only' && (
                <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" title="Only visible to core family members">
                  <Lock className="w-3 h-3" />
                  Core family
                </span>
              )}
            </div>
          </div>
        </div>

        {isOwnPost ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="post-menu-btn">
                <MoreHorizontal className="w-5 h-5 text-stone-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              <DropdownMenuItem
                onClick={handleDelete}
                className="text-red-600 cursor-pointer"
                disabled={deleting}
                data-testid="delete-post-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleting ? 'Deleting...' : 'Delete'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="post-menu-btn">
                <MoreHorizontal className="w-5 h-5 text-stone-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              <DropdownMenuItem
                onClick={() => setReportOpen(true)}
                className="text-stone-700 cursor-pointer"
                data-testid="report-post-btn"
              >
                <Flag className="w-4 h-4 mr-2" /> Report post
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleBlock}
                className="text-red-600 cursor-pointer"
                data-testid="block-user-btn"
              >
                <UserX className="w-4 h-4 mr-2" /> Block {post.user_name}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Content */}
      {post.content && (
        <p className="text-stone-800 mb-4 whitespace-pre-wrap">{post.content}</p>
      )}

      {/* Media */}
      {post.media_urls && post.media_urls.length > 0 && (
        <div className={`grid gap-2 mb-4 ${
          post.media_urls.length === 1 ? 'grid-cols-1' :
          post.media_urls.length === 2 ? 'grid-cols-2' :
          post.media_urls.length === 3 ? 'grid-cols-2' : 'grid-cols-2'
        }`}>
          {post.media_urls.map((fileId, index) => (
            <PostImage key={fileId} fileId={fileId} index={index} total={post.media_urls.length} />
          ))}
        </div>
      )}

      {/* Reactions Summary */}
      {totalReactions > 0 && (
        <div className="flex items-center gap-1 mb-3 text-sm text-stone-500">
          <div className="flex -space-x-1">
            {Object.entries(post.reactions_count || {}).slice(0, 3).map(([type]) => {
              const reaction = REACTION_TYPES.find(r => r.type === type);
              return reaction ? <span key={type}>{reaction.emoji}</span> : null;
            })}
          </div>
          <span>{totalReactions}</span>
        </div>
      )}

      {/* Actions */}
      {canInteract ? (
        <div className="flex items-center gap-2 pt-3 border-t border-stone-100">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowReactions(!showReactions)}
              className="text-stone-600 hover:text-teal-600 hover:bg-teal-50 rounded-full"
              data-testid="react-btn"
            >
              <Heart className="w-5 h-5 mr-1" />
              React
            </Button>
            
            {showReactions && (
              <div className="absolute bottom-full left-0 mb-2 bg-white rounded-full shadow-lg border border-stone-100 px-2 py-1 flex gap-1" data-testid="reaction-picker">
                {REACTION_TYPES.map(({ type, emoji, label }) => (
                  <button
                    key={type}
                    onClick={() => handleReaction(type)}
                    className="text-xl hover:scale-125 transition-transform p-1"
                    title={label}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowComments(!showComments)}
            className="text-stone-600 hover:text-teal-600 hover:bg-teal-50 rounded-full"
            data-testid="comments-btn"
          >
            <MessageCircle className="w-5 h-5 mr-1" />
            {post.comments_count > 0 ? `${post.comments_count} Comments` : 'Comment'}
          </Button>
        </div>
      ) : (
        post.comments_count > 0 && (
          <div className="flex items-center gap-2 pt-3 border-t border-stone-100">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowComments(!showComments)}
              className="text-stone-600 hover:text-teal-600 hover:bg-teal-50 rounded-full"
              data-testid="comments-btn"
            >
              <MessageCircle className="w-5 h-5 mr-1" />
              View {post.comments_count} Comments
            </Button>
          </div>
        )
      )}

      {/* Comments Section */}
      {showComments && (
        <CommentsSection postId={post.id} canComment={canInteract} />
      )}

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>Report this post</DialogTitle>
            <DialogDescription>
              We'll send this to your family admin. Abusive or inappropriate content can be hidden.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <RadioGroup value={reportReason} onValueChange={setReportReason} className="space-y-2">
              {[
                { v: 'spam', l: 'Spam' },
                { v: 'harassment', l: 'Harassment or bullying' },
                { v: 'hate', l: 'Hate speech' },
                { v: 'inappropriate', l: 'Inappropriate content' },
                { v: 'other', l: 'Something else' },
              ].map((o) => (
                <div key={o.v} className="flex items-center gap-2">
                  <RadioGroupItem value={o.v} id={`r-${o.v}`} data-testid={`report-reason-${o.v}`} />
                  <Label htmlFor={`r-${o.v}`} className="cursor-pointer">{o.l}</Label>
                </div>
              ))}
            </RadioGroup>
            <div>
              <Label htmlFor="report-details" className="text-stone-700 text-sm">Additional context (optional)</Label>
              <Textarea
                id="report-details"
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                placeholder="Anything else your admin should know?"
                className="rounded-xl border-stone-200 mt-1"
                rows={3}
                maxLength={500}
                data-testid="report-details-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              onClick={submitReport}
              disabled={submittingReport}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              data-testid="submit-report-btn"
            >
              <Flag className="w-4 h-4 mr-2" /> {submittingReport ? 'Sending…' : 'Send report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </article>
  );
}
